import type { Middleware } from "openapi-fetch";
import type { TokenGetter } from "../contextProvider/useApiToken";

/** Marks a job's own answer; mirrors `JOB_COMPLETED_HEADER` in `v2/endpoints/jobs/route.py`. */
export const JOB_COMPLETED_HEADER = "X-Job-Completed";

/** The 202 body: mirrors `job_pending_handler` in `appretio/main.py`. */
interface PendingJob {
  job: string;
}

function isPendingJob(body: unknown): body is PendingJob {
  return (
    typeof body === "object" &&
    body !== null &&
    typeof (body as Partial<PendingJob>).job === "string"
  );
}

function retryAfterMs(response: Response): number {
  const header = response.headers.get("Retry-After");
  const seconds = Number(header);
  return (header !== null && Number.isFinite(seconds) ? seconds : 1) * 1000;
}

/** The answer for a job the poll no longer finds: lost, retryable, as for a read whose pod died. */
function lost(): Response {
  return new Response(
    JSON.stringify({
      error: { message: "The job was lost. Try again." }
    }),
    {
      status: 503,
      headers: { "Content-Type": "application/json", "Retry-After": "5" }
    }
  );
}

/**
 * Whether a poll failed on its way, rather than delivering the job's
 * response: the API sheds a busy poll with 429, and a gateway error carries no
 * JSON error envelope. The job's own 5xx always does.
 */
async function isPollFailure(polled: Response): Promise<boolean> {
  if (polled.status === 429) {
    return true;
  }
  if (polled.status < 500) {
    return false;
  }
  const body: unknown = await polled
    .clone()
    .json()
    .catch(() => null);
  return !(typeof body === "object" && body !== null && "error" in body);
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      signal.removeEventListener("abort", abort);
      resolve();
    }
    function abort() {
      clearTimeout(timer);
      reject(signal.reason);
    }
    signal.addEventListener("abort", abort, { once: true });
  });
}

/**
 * Answer a request the server answered 202 (a job still running)
 * with the job's final response, so callers never see the 202.
 *
 * Polls `/v2/jobs/{id}` after each `Retry-After`, with a fresh token per
 * poll, until the status is not 202. A poll that throws or fails on its way
 * says nothing of the job, so it is asked again with backoff.
 */
export function createJobPollingMiddleware(getToken: TokenGetter): Middleware {
  return {
    async onResponse({ request, response, options }) {
      if (response.status !== 202) {
        return undefined;
      }
      const pending: unknown = await response
        .clone()
        .json()
        .catch(() => null);
      if (!isPendingJob(pending)) {
        return undefined;
      }
      // Unbound: the browser's fetch throws "Illegal invocation" when called
      // as a method of `options`.
      const { fetch, baseUrl } = options;
      const url = `${baseUrl}/v2/jobs/${encodeURIComponent(pending.job)}`;
      let delayMs = retryAfterMs(response);
      let failures = 0;
      let authFailures = 0;
      for (;;) {
        await wait(delayMs, request.signal);
        const token = await getToken();
        let polled: Response;
        try {
          polled = await fetch(
            new Request(url, {
              headers: { Authorization: `Bearer ${token}` },
              signal: request.signal
            })
          );
        } catch (error) {
          if (request.signal.aborted) {
            throw error;
          }
          failures += 1;
          delayMs = Math.min(2 ** (failures - 1), 30) * 1000;
          continue;
        }
        if (await isPollFailure(polled)) {
          failures += 1;
          delayMs = polled.headers.has("Retry-After")
            ? retryAfterMs(polled)
            : Math.min(2 ** (failures - 1), 30) * 1000;
          continue;
        }
        // The poll's credentials failing says nothing of the job: ask
        // again with a fresh token, then read it as lost, so a save keeps its
        // id. The job's own 401/403 carries the completed header.
        if (
          (polled.status === 401 || polled.status === 403) &&
          polled.headers.get(JOB_COMPLETED_HEADER) !== "true"
        ) {
          if (authFailures >= 3) {
            return lost();
          }
          authFailures += 1;
          delayMs = 1000;
          continue;
        }
        if (
          polled.status === 404 &&
          polled.headers.get(JOB_COMPLETED_HEADER) !== "true"
        ) {
          return lost();
        }
        if (polled.status !== 202) {
          return polled;
        }
        failures = 0;
        delayMs = retryAfterMs(polled);
      }
    }
  };
}
